import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { api, formatApiError } from '../../lib/api';
import { useDarkMode } from '../../context/DarkModeContext';
import { FieldRender } from '../../pages/PublicForm';
import {
  Plus, Trash2, Copy, ExternalLink, Eye, Funnel as FunnelIcon, Palette, Globe,
  ChevronUp, ChevronDown, Image as ImageIcon, Type, Mail, ShoppingBag, Sparkles,
  CheckCircle2, Code, MousePointerClick, RefreshCw, Inbox, Wand2, Loader2,
  PlayCircle, Star, Shield, Clock, Award, Users, Upload, HelpCircle,
} from 'lucide-react';

const STEP_TYPES = [
  { type: 'landing',     icon: Sparkles,         label: 'Landing / Hero', sub: 'Headline + sub + CTA' },
  { type: 'optin',       icon: Mail,             label: 'Opt-in form',    sub: 'Capture leads' },
  { type: 'sales',       icon: ShoppingBag,      label: 'Sales / Offer',  sub: 'Link to a product' },
  { type: 'upsell',      icon: MousePointerClick,label: 'Upsell',         sub: 'Post-purchase offer' },
  { type: 'thankyou',    icon: CheckCircle2,     label: 'Thank you',      sub: 'Confirmation page' },
  { type: 'custom_html', icon: Code,             label: 'Custom HTML',    sub: 'Paste any markup' },
];

const STEP_META = Object.fromEntries(STEP_TYPES.map((s) => [s.type, s]));

const DEFAULT_THEME = {
  primary_color: '#3368b8', secondary_color: '#26456f',
  background_color: '#ffffff', text_color: '#0f1923',
  accent_color: '#ff7a00', font_family: 'Inter, system-ui, sans-serif',
  heading_font_family: '', logo_url: '', favicon_url: '', source_url: '', site_name: '',
};

const newStep = (type = 'landing') => ({
  id: crypto.randomUUID(),
  type, title: STEP_META[type]?.label || 'Step',
  headline: '', subheadline: '', body_md: '', bullets: [], hero_image_url: '',
  cta_label: type === 'thankyou' ? '' : 'Continue', cta_action: 'next_step', cta_url: '',
  optin_fields: type === 'optin' ? [
    { id: crypto.randomUUID(), type: 'text', name: 'first', label: 'First name', required: true },
    { id: crypto.randomUUID(), type: 'email', name: 'email', label: 'Email', required: true },
  ] : [],
  success_message: '', product_id: '', upsell_discount_pct: null, custom_html: '',
});

// ── Top-level ─────────────────────────────────────────────────────────────────
const Funnels = ({ lob }) => {
  const darkMode = useDarkMode();
  const aBg = darkMode ? lob?.accentBgDark : lob?.accentBg;
  const aTc = darkMode ? lob?.accentTcDark : lob?.accentTc;
  const [funnels, setFunnels] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showAIWizard, setShowAIWizard] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    if (!lob) return;
    try { const { data } = await api.get(`/funnels?lob_id=${lob.id}`); setFunnels(data); }
    catch (e) { setErr(formatApiError(e)); }
  }, [lob?.id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (selected) {
      const upd = funnels.find((f) => f.id === selected.id);
      if (upd && upd.updated_at !== selected.updated_at) setSelected(upd);
    }
  }, [funnels, selected]);

  return (
    <>
      <div className="page-toolbar" data-testid="funnels-toolbar">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="page-title">Funnels</span>
          <span className="lob-badge" style={{ background: aBg }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: lob.accent, display: 'inline-block', marginRight: 4 }} />
            <span style={{ color: aTc }}>{lob.label}</span>
          </span>
        </div>
        <button className="cta-btn" style={{ background: lob.accent }} onClick={() => setShowCreate(true)} data-testid="add-funnel-btn">
          <Plus size={11} style={{ marginRight: 4 }} />New funnel
        </button>
        <button className="cta-btn ai" onClick={() => setShowAIWizard(true)} data-testid="ai-wizard-btn"
          style={{ background: 'linear-gradient(135deg, #8b5cf6, #ec4899)', marginLeft: 6 }}>
          <Wand2 size={11} style={{ marginRight: 4 }} />Build with AI
        </button>
      </div>

      {err && <div className="module-banner err">{err}</div>}

      <div className="fb-shell">
        <div className="fb-list-pane" data-testid="funnels-list">
          {funnels.length === 0 ? (
            <div style={{ padding: 20, fontSize: 11, color: 'var(--text-faded)' }} data-testid="funnels-empty">
              No funnels yet. Build a multi-step landing flow that captures leads and sells products.
            </div>
          ) : funnels.map((f) => (
            <div
              key={f.id}
              className={`fb-list-row${selected?.id === f.id ? ' active' : ''}`}
              onClick={() => setSelected(f)}
              data-testid={`funnel-row-${f.id}`}
            >
              <div className="lob-icon" style={{ background: aBg, width: 26, height: 26 }}>
                <FunnelIcon size={12} style={{ color: aTc }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                <div className="fb-list-meta">
                  {f.steps?.length || 0} step{(f.steps?.length || 0) === 1 ? '' : 's'}
                  {' · '}{f.submission_count || 0} leads
                  {' · '}{f.view_count || 0} views
                  {!f.is_active && ' · inactive'}
                </div>
              </div>
            </div>
          ))}
        </div>

        {selected ? (
          <FunnelBuilder
            funnel={selected}
            lob={lob}
            onSaved={(saved) => { load().then(() => setSelected(saved)); }}
            onDeleted={() => { setSelected(null); load(); }}
          />
        ) : (
          <div className="fb-canvas" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="empty-state" data-testid="funnels-pick">
              <FunnelIcon size={36} style={{ opacity: 0.25 }} />
              <div className="ttl">Pick a funnel to edit</div>
              <div className="sub">Or click "New funnel" to start from scratch.</div>
            </div>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateFunnelModal lob={lob} onClose={(saved) => {
          setShowCreate(false);
          if (saved) { load().then(() => setSelected(saved)); }
        }} />
      )}
      {showAIWizard && (
        <AIWizardModal lob={lob} onClose={(saved) => {
          setShowAIWizard(false);
          if (saved) { load().then(() => setSelected(saved)); }
        }} />
      )}
    </>
  );
};

// ── AI Wizard — generate full funnel via Claude + Nano Banana ─────────────────
const PRESET_OPTIONS = [
  { value: '',                 label: 'AI picks the structure' },
  { value: 'classic_lead',     label: 'Classic lead capture (Landing → Opt-in → Thanks)' },
  { value: 'lead_to_sale',     label: 'Lead → Sale (Landing → Opt-in → Sales → Thanks)' },
  { value: 'tripwire',         label: 'Tripwire (Landing → Sales → Upsell → Thanks)' },
  { value: 'webinar',          label: 'Webinar (Landing → Opt-in → Thanks → Sales)' },
  { value: 'long_form_sales',  label: 'Long-form sales (Landing → Sales → Upsell → Thanks)' },
];

const TONES = ['friendly', 'authoritative', 'playful', 'urgent', 'luxury', 'down-to-earth'];

const AIWizardModal = ({ lob, onClose }) => {
  const [mode, setMode] = useState('wizard');           // 'wizard' | 'brief'
  const [brief, setBrief] = useState('');
  const [wizard, setWizard] = useState({
    product_name: '', audience: '', pain_points: '', offer: '',
    price: '', tone: 'friendly', social_proof: '', goal: 'capture leads',
  });
  const [name, setName] = useState('');
  const [themeUrl, setThemeUrl] = useState('');
  const [preset, setPreset] = useState('');
  const [generateImages, setGenerateImages] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [err, setErr] = useState('');

  const setW = (k, v) => setWizard((w) => ({ ...w, [k]: v }));

  const canSubmit = useMemo(() => {
    if (busy) return false;
    if (mode === 'brief') return brief.trim().length >= 30;
    return wizard.product_name.trim() && (wizard.audience.trim() || wizard.offer.trim());
  }, [mode, brief, wizard, busy]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setErr('');
    setProgress(generateImages
      ? 'Writing your funnel pages with Claude… ~30-45s'
      : 'Writing your funnel pages with Claude… ~30-45s');
    try {
      const payload = {
        lob_id: lob.id,
        name: name || null,
        brief: mode === 'brief' ? brief : '',
        wizard: mode === 'wizard' ? Object.fromEntries(Object.entries(wizard).filter(([, v]) => v && String(v).trim())) : null,
        theme_url: themeUrl || null,
        preset: preset || null,
        suggest_structure: !preset,
        generate_images: generateImages,
        save: true,
        is_active: true,
      };
      const { data } = await api.post('/funnels/ai/generate', payload, { timeout: 120000 });
      // If images are still being generated in the background, close the modal anyway
      // and let the builder pick up the new image URLs as they land.
      onClose(data);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally { setBusy(false); setProgress(''); }
  };

  return (
    <div className="modal-overlay-v2" onClick={() => !busy && onClose(null)}>
      <form className="modal-card wide ai-wizard" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="ai-wizard-modal" style={{ maxWidth: 720 }}>
        <div className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Wand2 size={16} style={{ color: '#a855f7' }} />
          Build a funnel with AI
        </div>
        <div className="sub" style={{ fontSize: 11, color: 'var(--text-faded)', marginBottom: 14 }}>
          Powered by Claude Sonnet 4.5 + Nano Banana. Pick a flow (or let AI design one), describe your offer, and we'll write every page for you in ~30-90 seconds.
        </div>

        {/* Mode tabs */}
        <div className="ai-mode-tabs" data-testid="ai-mode-tabs">
          <button type="button" className={`ai-mode-tab${mode === 'wizard' ? ' active' : ''}`} onClick={() => setMode('wizard')} data-testid="ai-mode-wizard">Wizard</button>
          <button type="button" className={`ai-mode-tab${mode === 'brief' ? ' active' : ''}`} onClick={() => setMode('brief')} data-testid="ai-mode-brief">Free-text brief</button>
        </div>

        {mode === 'wizard' ? (
          <div className="ai-wizard-grid" data-testid="ai-wizard-form">
            <div>
              <label className="f-label">Product / offer name <span className="req">*</span></label>
              <input className="f-input" required value={wizard.product_name} onChange={(e) => setW('product_name', e.target.value)} placeholder="e.g. 90-Minute Focus Method" data-testid="aw-product-name" />
            </div>
            <div>
              <label className="f-label">Who is it for? <span className="req">*</span></label>
              <input className="f-input" value={wizard.audience} onChange={(e) => setW('audience', e.target.value)} placeholder="e.g. busy founders, 30-45 yo" data-testid="aw-audience" />
            </div>
            <div className="span-2">
              <label className="f-label">Pain points it solves</label>
              <input className="f-input" value={wizard.pain_points} onChange={(e) => setW('pain_points', e.target.value)} placeholder="meetings + Slack destroy deep work" />
            </div>
            <div className="span-2">
              <label className="f-label">The offer / what they get</label>
              <input className="f-input" value={wizard.offer} onChange={(e) => setW('offer', e.target.value)} placeholder="e.g. 4 video modules + 30-day guarantee" />
            </div>
            <div>
              <label className="f-label">Price</label>
              <input className="f-input" value={wizard.price} onChange={(e) => setW('price', e.target.value)} placeholder="$97" />
            </div>
            <div>
              <label className="f-label">Tone of voice</label>
              <select className="f-select" value={wizard.tone} onChange={(e) => setW('tone', e.target.value)}>
                {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="span-2">
              <label className="f-label">Social proof / credibility (optional)</label>
              <input className="f-input" value={wizard.social_proof} onChange={(e) => setW('social_proof', e.target.value)} placeholder="e.g. 2,300+ alumni, featured in Forbes" />
            </div>
          </div>
        ) : (
          <div>
            <label className="f-label">Describe your funnel</label>
            <textarea
              className="f-textarea"
              rows={7}
              required={mode === 'brief'}
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="I sell a $97 productivity course teaching busy founders how to build a daily 90-minute focus routine. Audience: 30-45yo entrepreneurs. Pain: meetings & Slack destroy deep work. Offer: 4 video modules + 30-day money-back guarantee. Social proof: 2,300+ alumni…"
              data-testid="aw-brief"
            />
            <div style={{ fontSize: 10, color: 'var(--text-faded)', marginTop: 4 }}>
              {brief.length}/30+ characters (~{Math.max(0, 30 - brief.length)} more to enable)
            </div>
          </div>
        )}

        {/* Common controls */}
        <div className="modal-row" style={{ marginTop: 14 }}>
          <div>
            <label className="f-label">Funnel name (optional — AI will generate one)</label>
            <input className="f-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Auto" data-testid="aw-name" />
          </div>
          <div>
            <label className="f-label">Funnel flow</label>
            <select className="f-select" value={preset} onChange={(e) => setPreset(e.target.value)} data-testid="aw-preset">
              {PRESET_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
        </div>

        <label className="f-label" style={{ marginTop: 10 }}>Brand URL (optional — imports colors, fonts, logo)</label>
        <input className="f-input" type="url" value={themeUrl} onChange={(e) => setThemeUrl(e.target.value)} placeholder="https://your-site.com" data-testid="aw-theme-url" />

        <label className="f-checkbox" style={{ marginTop: 12 }} data-testid="aw-generate-images-label">
          <input type="checkbox" checked={generateImages} onChange={() => setGenerateImages((v) => !v)} data-testid="aw-generate-images" />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <ImageIcon size={11} /> Also generate a hero image for each step (Nano Banana — adds ~15s per step)
          </span>
        </label>

        {err && <div className="f-error" data-testid="aw-error">{err}</div>}
        {busy && (
          <div className="ai-progress" data-testid="aw-progress">
            <Loader2 size={14} className="spin" /> {progress}
          </div>
        )}

        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => onClose(null)} disabled={busy}>Cancel</button>
          <button type="submit" className="btn-primary ai" disabled={!canSubmit} data-testid="aw-submit"
            style={{ background: 'linear-gradient(135deg, #8b5cf6, #ec4899)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {busy ? <><Loader2 size={13} className="spin" /> Generating…</> : <><Sparkles size={13} /> Generate funnel</>}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Create Funnel modal: name + theme scrape + initial steps ──────────────────
const CreateFunnelModal = ({ lob, onClose }) => {
  const [name, setName] = useState('');
  const [scrapeUrl, setScrapeUrl] = useState('');
  const [theme, setTheme] = useState({ ...DEFAULT_THEME });
  const [scraping, setScraping] = useState(false);
  const [scrapeMsg, setScrapeMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const scrape = async () => {
    if (!scrapeUrl.trim()) return;
    setScraping(true); setScrapeMsg(''); setErr('');
    try {
      const { data } = await api.post('/funnels/scrape-theme', { url: scrapeUrl });
      setTheme({ ...DEFAULT_THEME, ...data });
      setScrapeMsg(`Theme imported from ${data.site_name || data.source_url}`);
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setScraping(false); }
  };

  const create = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try {
      const steps = [
        { ...newStep('landing'), headline: name || 'Welcome', subheadline: 'Built with LegacyCRM funnels.' },
        newStep('optin'),
        { ...newStep('thankyou'), headline: 'Thanks — we\'ll be in touch.', cta_label: '' },
      ];
      const { data } = await api.post('/funnels', {
        lob_id: lob.id, name, theme, steps, is_active: true,
      });
      onClose(data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay-v2" onClick={() => onClose(null)}>
      <form className="modal-card wide" onClick={(e) => e.stopPropagation()} onSubmit={create} data-testid="create-funnel-modal">
        <div className="modal-title">New funnel</div>
        <label className="f-label">Funnel name</label>
        <input className="f-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. October Launch Funnel" data-testid="cf-name" />

        <div className="feature-card" style={{ margin: '14px 0 0' }}>
          <div className="ttl" style={{ fontSize: 12 }}><Globe size={11} /> Import theme from a website <span className="tag-pill muted" style={{ fontSize: 8 }}>optional</span></div>
          <div className="sub">Paste a URL — we'll grab colors, fonts, logo, and favicon so the funnel matches your brand.</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <input
              className="f-input" type="url" placeholder="https://your-site.com"
              value={scrapeUrl} onChange={(e) => setScrapeUrl(e.target.value)}
              data-testid="cf-scrape-url"
            />
            <button type="button" className="btn-secondary" onClick={scrape} disabled={scraping || !scrapeUrl.trim()} data-testid="cf-scrape-btn">
              {scraping ? <RefreshCw size={11} className="spin" /> : <Palette size={11} />} {scraping ? 'Scanning…' : 'Import'}
            </button>
          </div>
          {scrapeMsg && <div style={{ fontSize: 10, color: 'var(--success, #64cb13)', marginTop: 6 }} data-testid="cf-scrape-msg">{scrapeMsg}</div>}
          <ThemePreview theme={theme} />
        </div>

        {err && <div className="f-error" data-testid="cf-error">{err}</div>}
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => onClose(null)}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy || !name} data-testid="cf-submit">{busy ? 'Creating…' : 'Create funnel'}</button>
        </div>
      </form>
    </div>
  );
};

const ThemePreview = ({ theme }) => {
  const swatches = [
    { k: 'primary_color', label: 'Primary' },
    { k: 'secondary_color', label: 'Secondary' },
    { k: 'background_color', label: 'Background' },
    { k: 'text_color', label: 'Text' },
    { k: 'accent_color', label: 'Accent' },
  ];
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      {theme.logo_url && <img src={theme.logo_url} alt="" style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'contain', background: '#0001', padding: 2 }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
      {swatches.map((s) => (
        <div key={s.k} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <div style={{ width: 24, height: 24, background: theme[s.k], borderRadius: 4, border: '0.5px solid var(--stroke-light)' }} title={`${s.label}: ${theme[s.k]}`} />
          <span style={{ fontSize: 8, color: 'var(--text-faded)' }}>{s.label}</span>
        </div>
      ))}
      <div style={{ flex: 1 }} />
      <div style={{ fontSize: 9, color: 'var(--text-faded)', maxWidth: 160, textAlign: 'right' }}>
        Font: <span style={{ fontFamily: theme.font_family }}>{(theme.font_family || '').split(',')[0]}</span>
      </div>
    </div>
  );
};

// ── Builder ────────────────────────────────────────────────────────────────────
const FunnelBuilder = ({ funnel, lob, onSaved, onDeleted }) => {
  const [draft, setDraft] = useState(funnel);
  const [selectedStepIdx, setSelectedStepIdx] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [showSubs, setShowSubs] = useState(false);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    setDraft(funnel); setDirty(false); setSelectedStepIdx(0);
  }, [funnel?.id]);

  useEffect(() => {
    api.get(`/products?lob_id=${lob.id}`).then((r) => setProducts(r.data || [])).catch(() => {});
  }, [lob?.id]);

  // Poll for image generation progress while the background task is running
  useEffect(() => {
    if (!funnel?.id || !draft?.images_pending) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/funnels/${funnel.id}`);
        if (cancelled) return;
        setDraft((d) => ({
          ...d,
          steps: data.steps || d.steps,
          images_pending: data.images_pending,
          images_completed: data.images_completed,
          images_total: data.images_total,
        }));
        if (!data.images_pending) onSaved(data);
      } catch (_) { /* keep polling */ }
    };
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [funnel?.id, draft?.images_pending]); // eslint-disable-line react-hooks/exhaustive-deps

  const publicUrl = useMemo(() => `${window.location.origin}/funnel/${draft.slug}`, [draft.slug]);

  const update = (patch) => { setDraft((d) => ({ ...d, ...patch })); setDirty(true); };
  const updateStep = (idx, patch) => {
    setDraft((d) => {
      const steps = [...(d.steps || [])];
      steps[idx] = { ...steps[idx], ...patch };
      return { ...d, steps };
    });
    setDirty(true);
  };
  const addStep = (type) => {
    setDraft((d) => ({ ...d, steps: [...(d.steps || []), newStep(type)] }));
    setSelectedStepIdx((d) => (draft.steps?.length || 0));
    setDirty(true);
  };
  const removeStep = (idx) => {
    if (!window.confirm('Remove this step?')) return;
    setDraft((d) => ({ ...d, steps: d.steps.filter((_, i) => i !== idx) }));
    setSelectedStepIdx((i) => Math.max(0, Math.min(i, (draft.steps?.length || 1) - 2)));
    setDirty(true);
  };
  const moveStep = (idx, dir) => {
    setDraft((d) => {
      const steps = [...(d.steps || [])];
      const j = idx + dir;
      if (j < 0 || j >= steps.length) return d;
      [steps[idx], steps[j]] = [steps[j], steps[idx]];
      return { ...d, steps };
    });
    setSelectedStepIdx((i) => i + dir);
    setDirty(true);
  };

  const save = async () => {
    setBusy(true); setErr('');
    try {
      const { data } = await api.patch(`/funnels/${funnel.id}`, {
        name: draft.name, description: draft.description, is_active: draft.is_active,
        theme: draft.theme, steps: draft.steps, slug: draft.slug,
      });
      onSaved(data);
      setDirty(false);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete funnel "${funnel.name}"? This removes all submissions too.`)) return;
    try { await api.delete(`/funnels/${funnel.id}`); onDeleted(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  const step = draft.steps?.[selectedStepIdx];

  return (
    <div className="fb-builder">
      {/* Left: step list + funnel meta */}
      <div className="fb-fields-side">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <input
            className="f-input" style={{ flex: 1, fontSize: 12, fontWeight: 600 }}
            value={draft.name} onChange={(e) => update({ name: e.target.value })}
            data-testid="fb-name"
          />
        </div>
        <label className="f-checkbox" style={{ fontSize: 11 }}>
          <input type="checkbox" checked={!!draft.is_active} onChange={() => update({ is_active: !draft.is_active })} data-testid="fb-active" />
          Active (publicly visible)
        </label>

        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-faded)', margin: '14px 0 6px' }}>Steps</div>
        {(draft.steps || []).map((s, i) => {
          const M = STEP_META[s.type] || STEP_META.landing;
          const Icon = M.icon;
          return (
            <div
              key={s.id}
              onClick={() => setSelectedStepIdx(i)}
              className={`fb-list-row${selectedStepIdx === i ? ' active' : ''}`}
              style={{ padding: '7px 8px', borderRadius: 6, marginBottom: 4, border: '0.5px solid var(--stroke-ghost)' }}
              data-testid={`step-row-${i}`}
            >
              <Icon size={12} style={{ opacity: 0.7 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i + 1}. {s.title || M.label}</div>
                <div className="fb-list-meta">{M.label}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <button type="button" className="dp-btn" style={{ padding: '1px 4px' }} onClick={(e) => { e.stopPropagation(); moveStep(i, -1); }} disabled={i === 0} data-testid={`step-up-${i}`}><ChevronUp size={9} /></button>
                <button type="button" className="dp-btn" style={{ padding: '1px 4px' }} onClick={(e) => { e.stopPropagation(); moveStep(i, 1); }} disabled={i === draft.steps.length - 1} data-testid={`step-down-${i}`}><ChevronDown size={9} /></button>
              </div>
            </div>
          );
        })}

        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-faded)', margin: '14px 0 6px' }}>Add step</div>
        {STEP_TYPES.map((t) => {
          const I = t.icon;
          return (
            <button key={t.type} type="button" className="fb-add-btn" onClick={() => addStep(t.type)} data-testid={`add-step-${t.type}`}>
              <I size={11} />{t.label}
            </button>
          );
        })}

        <div style={{ marginTop: 18, borderTop: '0.5px solid var(--stroke-lighter)', paddingTop: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-faded)', marginBottom: 6 }}>Theme</div>
          <ThemeEditor theme={draft.theme || DEFAULT_THEME} onChange={(t) => update({ theme: t })} />
        </div>
      </div>

      {/* Right: canvas — step editor + preview */}
      <div className="fb-canvas" data-testid="fb-canvas">
        <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <a href={publicUrl} target="_blank" rel="noreferrer" className="dp-btn" data-testid="fb-open-public">
              <ExternalLink size={10} style={{ marginRight: 3 }} />Open public funnel
            </a>
            <button type="button" className="dp-btn" onClick={() => navigator.clipboard.writeText(publicUrl)} data-testid="fb-copy-url">
              <Copy size={10} style={{ marginRight: 3 }} />Copy URL
            </button>
            <button type="button" className="dp-btn" onClick={() => setShowSubs(true)} data-testid="fb-view-subs">
              <Inbox size={10} style={{ marginRight: 3 }} />Submissions ({draft.submission_count || 0})
            </button>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="dp-btn" style={{ color: 'var(--error)' }} onClick={del} data-testid="fb-delete"><Trash2 size={10} /></button>
            <button
              type="button"
              className={`btn-primary ${dirty ? '' : 'disabled'}`}
              disabled={busy || !dirty}
              onClick={save}
              data-testid="fb-save"
              style={{ background: dirty ? lob.accent : undefined }}
            >
              {busy ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
            </button>
          </div>
        </div>

        {err && <div className="module-banner err" style={{ maxWidth: 760, margin: '0 auto 10px' }}>{err}</div>}

        {draft?.images_pending && (
          <div className="ai-image-banner" data-testid="fb-image-progress" style={{ maxWidth: 760, margin: '0 auto 10px' }}>
            <Loader2 size={13} className="spin" />
            <span>
              Generating hero images with Nano Banana…
              {' '}
              <b>{draft.images_completed || 0} / {draft.images_total || '?'}</b> done.
              They'll appear here as soon as each one is ready.
            </span>
          </div>
        )}

        {step ? (
          <StepEditor
            step={step}
            stepIdx={selectedStepIdx}
            theme={draft.theme || DEFAULT_THEME}
            products={products}
            funnelId={funnel.id}
            onChange={(patch) => updateStep(selectedStepIdx, patch)}
            onRemove={() => removeStep(selectedStepIdx)}
            onAIApplied={() => { setDirty(false); /* AI already auto-saved server-side */ }}
          />
        ) : (
          <div className="empty-state"><div className="ttl">No steps yet</div><div className="sub">Add your first step from the left panel.</div></div>
        )}
      </div>

      {showSubs && <SubmissionsModal funnel={funnel} onClose={() => setShowSubs(false)} />}
    </div>
  );
};

// ── Step editor ────────────────────────────────────────────────────────────────
const StepEditor = ({ step, stepIdx, theme, products, onChange, onRemove, funnelId, onAIApplied }) => {
  const M = STEP_META[step.type] || STEP_META.landing;
  const Icon = M.icon;
  const oneTimeProducts = (products || []).filter((p) => p.interval === 'one_time' && p.is_active !== false);
  const allProducts = (products || []).filter((p) => p.is_active !== false);
  const [aiBusy, setAiBusy] = useState('');                // '' | 'copy' | 'image'
  const [aiInstr, setAiInstr] = useState('');
  const [aiErr, setAiErr] = useState('');

  const regenCopy = async () => {
    if (!funnelId || !step.id) return;
    setAiBusy('copy'); setAiErr('');
    try {
      const { data } = await api.post(`/funnels/${funnelId}/steps/${step.id}/ai/regenerate`,
        { extra_instructions: aiInstr || null, auto_save: true },
        { timeout: 60000 });
      onChange({
        headline: data.headline,
        subheadline: data.subheadline,
        body_md: data.body_md,
        bullets: data.bullets,
        cta_label: data.cta_label || step.cta_label,
      });
      onAIApplied?.();
      setAiInstr('');
    } catch (ex) { setAiErr(formatApiError(ex)); }
    finally { setAiBusy(''); }
  };

  const genImage = async () => {
    if (!funnelId || !step.id) return;
    setAiBusy('image'); setAiErr('');
    try {
      const { data } = await api.post(`/funnels/${funnelId}/steps/${step.id}/ai/generate-image`,
        { prompt: aiInstr || null, auto_save: true },
        { timeout: 90000 });
      onChange({ hero_image_url: data.hero_image_url });
      onAIApplied?.();
      setAiInstr('');
    } catch (ex) { setAiErr(formatApiError(ex)); }
    finally { setAiBusy(''); }
  };

  return (
    <div className="fb-canvas-card" data-testid={`step-editor-${stepIdx}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <div style={{ background: theme.primary_color + '22', color: theme.primary_color, padding: '4px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Icon size={11} />Step {stepIdx + 1} · {M.label}
        </div>
        <input
          className="f-input" style={{ flex: 1, fontSize: 12 }}
          placeholder="Internal title (only you see this)"
          value={step.title || ''} onChange={(e) => onChange({ title: e.target.value })}
          data-testid={`step-title-${stepIdx}`}
        />
        <button type="button" className="dp-btn" style={{ color: 'var(--error)' }} onClick={onRemove} data-testid={`step-remove-${stepIdx}`}><Trash2 size={10} /></button>
      </div>

      {/* AI controls — only show when funnel is already saved (we have its id) */}
      {funnelId && (
        <div className="ai-step-bar" data-testid={`ai-step-bar-${stepIdx}`}>
          <input
            className="f-input ai-instr"
            placeholder="Optional: tell AI how to rewrite this step (e.g. 'make it punchier with urgency')"
            value={aiInstr}
            onChange={(e) => setAiInstr(e.target.value)}
            data-testid={`ai-instr-${stepIdx}`}
          />
          <button
            type="button"
            className="btn-ai"
            onClick={regenCopy}
            disabled={!!aiBusy}
            data-testid={`ai-regen-copy-${stepIdx}`}
            title="Rewrite this step with Claude Sonnet 4.5"
          >
            {aiBusy === 'copy' ? <Loader2 size={11} className="spin" /> : <Wand2 size={11} />}
            <span>Rewrite copy</span>
          </button>
          {(step.type === 'landing' || step.type === 'sales' || step.type === 'upsell') && (
            <button
              type="button"
              className="btn-ai img"
              onClick={genImage}
              disabled={!!aiBusy}
              data-testid={`ai-gen-image-${stepIdx}`}
              title="Generate a hero image with Nano Banana"
            >
              {aiBusy === 'image' ? <Loader2 size={11} className="spin" /> : <ImageIcon size={11} />}
              <span>Generate image</span>
            </button>
          )}
        </div>
      )}
      {aiErr && <div className="f-error" data-testid={`ai-err-${stepIdx}`}>{aiErr}</div>}

      {/* Content fields */}
      {step.type !== 'custom_html' && (
        <>
          <label className="f-label">Headline</label>
          <input className="f-input" value={step.headline || ''} onChange={(e) => onChange({ headline: e.target.value })} placeholder="The headline visitors see first" data-testid={`step-headline-${stepIdx}`} />

          <label className="f-label" style={{ marginTop: 8 }}>Subheadline</label>
          <input className="f-input" value={step.subheadline || ''} onChange={(e) => onChange({ subheadline: e.target.value })} placeholder="One supporting sentence" data-testid={`step-subheadline-${stepIdx}`} />

          <label className="f-label" style={{ marginTop: 8 }}>Body (markdown allowed)</label>
          <textarea className="f-textarea" rows={4} value={step.body_md || ''} onChange={(e) => onChange({ body_md: e.target.value })} placeholder="Longer copy, story, or pitch" data-testid={`step-body-${stepIdx}`} />

          <label className="f-label" style={{ marginTop: 8 }}>Hero image (upload or paste URL)</label>
          <ImageUploadField
            value={step.hero_image_url || ''}
            onChange={(url) => onChange({ hero_image_url: url })}
            placeholder="https://… or click upload"
            testid={`step-hero-image-upload-${stepIdx}`}
          />
        </>
      )}

      {/* Sales / Upsell — product picker */}
      {(step.type === 'sales' || step.type === 'upsell') && (
        <>
          <label className="f-label" style={{ marginTop: 10 }}>Product to sell</label>
          <select className="f-select" value={step.product_id || ''} onChange={(e) => onChange({ product_id: e.target.value || null })} data-testid={`step-product-${stepIdx}`}>
            <option value="">— Pick a product —</option>
            {(step.type === 'upsell' ? oneTimeProducts : allProducts).map((p) => (
              <option key={p.id} value={p.id}>
                {`${p.name} — $${p.price}${p.interval !== 'one_time' ? ` / ${p.interval}` : ''}`}
              </option>
            ))}
          </select>
          {step.type === 'upsell' && (
            <>
              <label className="f-label" style={{ marginTop: 8 }}>Upsell discount % (optional)</label>
              <input className="f-input" type="number" min="0" max="100" value={step.upsell_discount_pct ?? ''} onChange={(e) => onChange({ upsell_discount_pct: e.target.value === '' ? null : Number(e.target.value) })} placeholder="e.g. 20" />
            </>
          )}
        </>
      )}

      {/* Opt-in fields */}
      {step.type === 'optin' && (
        <OptinFieldsEditor
          step={step}
          onChange={onChange}
          stepIdx={stepIdx}
        />
      )}

      {/* CTA */}
      {step.type !== 'thankyou' && step.type !== 'custom_html' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <div style={{ flex: 1 }}>
              <label className="f-label">CTA label</label>
              <input className="f-input" value={step.cta_label || ''} onChange={(e) => onChange({ cta_label: e.target.value })} placeholder="Continue" data-testid={`step-cta-${stepIdx}`} />
            </div>
            <div style={{ width: 180 }}>
              <label className="f-label">CTA action</label>
              <select className="f-select" value={step.cta_action || 'next_step'} onChange={(e) => onChange({ cta_action: e.target.value })}>
                <option value="next_step">Go to next step</option>
                <option value="checkout">Buy this product</option>
                <option value="external_url">Open external URL</option>
              </select>
            </div>
          </div>
          {step.cta_action === 'external_url' && (
            <>
              <label className="f-label" style={{ marginTop: 8 }}>External URL</label>
              <input className="f-input" type="url" value={step.cta_url || ''} onChange={(e) => onChange({ cta_url: e.target.value })} placeholder="https://…" />
            </>
          )}
        </>
      )}

      {/* Custom HTML / 3rd-party embed code — universal, available on EVERY step type.
          Scripts inside this HTML execute on the public page (CustomHtmlEmbed handles it). */}
      <div className="fb-section-hd"><Code size={11} /> Custom HTML / 3rd-party embed (optional)</div>
      <div style={{ fontSize: 10, opacity: 0.7, marginBottom: 4 }}>
        {step.type === 'custom_html'
          ? 'This step renders ONLY this HTML — no hero/header generated by the funnel.'
          : 'Paste any embed code (Trulli forms, Calendly, Typeform, custom widgets). Scripts will execute. Appears below the hero on this step.'}
      </div>
      <textarea
        className="f-textarea"
        rows={step.type === 'custom_html' ? 10 : 5}
        value={step.custom_html || ''}
        onChange={(e) => onChange({ custom_html: e.target.value })}
        style={{ fontFamily: 'monospace', fontSize: 11 }}
        placeholder={step.type === 'custom_html' ? '<h1>Anything you want</h1>' : '<div id="trulli-marketplace"></div>\n<script src="https://…"></script>'}
        data-testid={`step-custom-html-${stepIdx}`}
      />

      {step.type === 'thankyou' && (
        <>
          <label className="f-label" style={{ marginTop: 10 }}>Success message (optional override)</label>
          <input className="f-input" value={step.success_message || ''} onChange={(e) => onChange({ success_message: e.target.value })} placeholder="Defaults to the headline above" />
        </>
      )}

      {/* ── Sales-page sections (video, features, testimonials, guarantee, urgency) ── */}
      {step.type !== 'custom_html' && step.type !== 'thankyou' && (
        <SalesPageExtras step={step} onChange={onChange} stepIdx={stepIdx} />
      )}
    </div>
  );
};

// ── Sales-page extras: video, features, testimonials, guarantee, urgency,
// hero layout, how-it-works, trusted-by, recent buyers ──────────────────────
const SalesPageExtras = ({ step, onChange, stepIdx }) => {
  const features = step.features || [];
  const testimonials = step.testimonials || [];
  const howSteps = step.how_it_works || [];
  const trusted = step.trusted_by || [];
  const buyers = step.recent_buyers || [];

  const updateFeature = (i, patch) => onChange({ features: features.map((f, idx) => idx === i ? { ...f, ...patch } : f) });
  const addFeature = () => onChange({ features: [...features, { title: '', description: '', icon: 'Sparkles' }] });
  const removeFeature = (i) => onChange({ features: features.filter((_, idx) => idx !== i) });

  const updateTestimonial = (i, patch) => onChange({ testimonials: testimonials.map((t, idx) => idx === i ? { ...t, ...patch } : t) });
  const addTestimonial = () => onChange({ testimonials: [...testimonials, { name: '', role: '', quote: '', rating: 5 }] });
  const removeTestimonial = (i) => onChange({ testimonials: testimonials.filter((_, idx) => idx !== i) });

  const updateHow = (i, patch) => onChange({ how_it_works: howSteps.map((h, idx) => idx === i ? { ...h, ...patch } : h) });
  const addHow = () => onChange({ how_it_works: [...howSteps, { title: '', description: '', icon: 'Sparkles' }] });
  const removeHow = (i) => onChange({ how_it_works: howSteps.filter((_, idx) => idx !== i) });

  const updateTrusted = (i, patch) => onChange({ trusted_by: trusted.map((t, idx) => idx === i ? { ...t, ...patch } : t) });
  const addTrusted = () => onChange({ trusted_by: [...trusted, { name: '', logo_url: '' }] });
  const removeTrusted = (i) => onChange({ trusted_by: trusted.filter((_, idx) => idx !== i) });

  const updateBuyer = (i, patch) => onChange({ recent_buyers: buyers.map((b, idx) => idx === i ? { ...b, ...patch } : b) });
  const addBuyer = () => onChange({ recent_buyers: [...buyers, { name: '', location: '', time_ago: 'just now' }] });
  const removeBuyer = (i) => onChange({ recent_buyers: buyers.filter((_, idx) => idx !== i) });

  const faq = step.faq || [];
  const updateFaq = (i, patch) => onChange({ faq: faq.map((q, idx) => idx === i ? { ...q, ...patch } : q) });
  const addFaq = () => onChange({ faq: [...faq, { question: '', answer: '' }] });
  const removeFaq = (i) => onChange({ faq: faq.filter((_, idx) => idx !== i) });

  return (
    <>
      <div className="fb-section-hd"><Eye size={11} /> Hero layout</div>
      <select className="f-select" value={step.hero_layout || 'center'} onChange={(e) => onChange({ hero_layout: e.target.value })} data-testid={`step-hero-layout-${stepIdx}`}>
        <option value="center">Centered (image/video below text)</option>
        <option value="side-right">Side-by-side — text left, media right</option>
        <option value="side-left">Side-by-side — media left, text right</option>
      </select>

      <div className="fb-section-hd"><PlayCircle size={11} /> Video — upload, paste YouTube/Vimeo URL, or direct mp4</div>
      <VideoUploadField
        value={step.video_url || ''}
        onChange={(url) => onChange({ video_url: url })}
        placeholder="YouTube, Vimeo or direct video URL"
        testid={`step-video-${stepIdx}`}
      />

      {(step.type === 'sales' || step.type === 'upsell') && (
        <>
          <div className="fb-section-hd"><Clock size={11} /> Urgency / Scarcity (optional)</div>
          <input
            className="f-input"
            value={step.urgency || ''}
            onChange={(e) => onChange({ urgency: e.target.value })}
            placeholder="e.g. Only 100 spots — closes Friday"
            data-testid={`step-urgency-${stepIdx}`}
          />
          <label className="f-label" style={{ marginTop: 6, fontSize: 10, opacity: 0.7 }}>
            Countdown ends at (optional — shows a live timer next to the urgency text)
          </label>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              className="f-input"
              type="datetime-local"
              style={{ flex: 1 }}
              value={step.urgency_ends_at ? new Date(step.urgency_ends_at).toISOString().slice(0, 16) : ''}
              onChange={(e) => onChange({ urgency_ends_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
              data-testid={`step-urgency-ends-${stepIdx}`}
            />
            {step.urgency_ends_at && (
              <button
                type="button"
                className="dp-btn"
                onClick={() => onChange({ urgency_ends_at: null })}
                title="Clear countdown"
                data-testid={`step-urgency-ends-clear-${stepIdx}`}
              >
                <Trash2 size={10} />
              </button>
            )}
          </div>

          <div className="fb-section-hd"><Shield size={11} /> Guarantee (optional)</div>
          <input
            className="f-input"
            value={step.guarantee || ''}
            onChange={(e) => onChange({ guarantee: e.target.value })}
            placeholder="e.g. 30-day money-back guarantee"
            data-testid={`step-guarantee-${stepIdx}`}
          />
        </>
      )}

      <div className="fb-section-hd"><Award size={11} /> Trusted by ({trusted.length})</div>
      {trusted.map((t, i) => (
        <div className="fb-subblock" key={i} data-testid={`trusted-${stepIdx}-${i}`}>
          <div className="grid-2">
            <input className="f-input" placeholder="Company / publication name" value={t.name || ''} onChange={(e) => updateTrusted(i, { name: e.target.value })} />
            <ImageUploadField
              value={t.logo_url || ''}
              onChange={(url) => updateTrusted(i, { logo_url: url })}
              placeholder="Logo URL (or upload)"
              testid={`trusted-logo-${stepIdx}-${i}`}
            />
          </div>
          <button type="button" className="dp-btn" style={{ marginTop: 4, color: 'var(--error)' }} onClick={() => removeTrusted(i)}><Trash2 size={9} /> Remove</button>
        </div>
      ))}
      <button type="button" className="fb-add-btn" onClick={addTrusted} data-testid={`trusted-add-${stepIdx}`}>
        <Plus size={11} /> Add company / publication
      </button>

      <div className="fb-section-hd"><Sparkles size={11} /> Features ({features.length})</div>
      {features.map((f, i) => (
        <div className="fb-subblock" key={i} data-testid={`feature-${stepIdx}-${i}`}>
          <div className="grid-2">
            <input className="f-input" placeholder="Title" value={f.title || ''} onChange={(e) => updateFeature(i, { title: e.target.value })} />
            <input className="f-input" placeholder="Icon (e.g. Zap, Shield, Clock)" value={f.icon || ''} onChange={(e) => updateFeature(i, { icon: e.target.value })} />
          </div>
          <textarea className="f-textarea" rows={2} style={{ marginTop: 6 }} placeholder="One-sentence benefit" value={f.description || ''} onChange={(e) => updateFeature(i, { description: e.target.value })} />
          <button type="button" className="dp-btn" style={{ marginTop: 4, color: 'var(--error)' }} onClick={() => removeFeature(i)} data-testid={`feature-remove-${stepIdx}-${i}`}><Trash2 size={9} /> Remove</button>
        </div>
      ))}
      <button type="button" className="fb-add-btn" onClick={addFeature} data-testid={`feature-add-${stepIdx}`}>
        <Plus size={11} /> Add feature
      </button>

      <div className="fb-section-hd"><MousePointerClick size={11} /> How it works ({howSteps.length})</div>
      {howSteps.map((h, i) => (
        <div className="fb-subblock" key={i} data-testid={`how-${stepIdx}-${i}`}>
          <div className="grid-2">
            <input className="f-input" placeholder={`Step ${i + 1} title`} value={h.title || ''} onChange={(e) => updateHow(i, { title: e.target.value })} />
            <input className="f-input" placeholder="Icon" value={h.icon || ''} onChange={(e) => updateHow(i, { icon: e.target.value })} />
          </div>
          <textarea className="f-textarea" rows={2} style={{ marginTop: 6 }} placeholder="What happens at this step" value={h.description || ''} onChange={(e) => updateHow(i, { description: e.target.value })} />
          <button type="button" className="dp-btn" style={{ marginTop: 4, color: 'var(--error)' }} onClick={() => removeHow(i)}><Trash2 size={9} /> Remove</button>
        </div>
      ))}
      <button type="button" className="fb-add-btn" onClick={addHow} data-testid={`how-add-${stepIdx}`}>
        <Plus size={11} /> Add step
      </button>

      <div className="fb-section-hd"><Star size={11} /> Testimonials ({testimonials.length})</div>
      {testimonials.map((t, i) => (
        <div className="fb-subblock" key={i} data-testid={`testimonial-${stepIdx}-${i}`}>
          <div className="grid-2">
            <input className="f-input" placeholder="Name" value={t.name || ''} onChange={(e) => updateTestimonial(i, { name: e.target.value })} />
            <input className="f-input" placeholder="Role / company" value={t.role || ''} onChange={(e) => updateTestimonial(i, { role: e.target.value })} />
          </div>
          <textarea className="f-textarea" rows={3} style={{ marginTop: 6 }} placeholder="Quote" value={t.quote || ''} onChange={(e) => updateTestimonial(i, { quote: e.target.value })} />
          <div className="grid-2" style={{ marginTop: 6 }}>
            <input className="f-input" type="number" min="1" max="5" placeholder="Rating 1-5" value={t.rating ?? 5} onChange={(e) => updateTestimonial(i, { rating: Math.min(5, Math.max(1, Number(e.target.value) || 5)) })} />
            <ImageUploadField
              value={t.avatar_url || ''}
              onChange={(url) => updateTestimonial(i, { avatar_url: url })}
              placeholder="Avatar URL (or upload)"
              testid={`testimonial-avatar-${stepIdx}-${i}`}
            />
          </div>
          <button type="button" className="dp-btn" style={{ marginTop: 4, color: 'var(--error)' }} onClick={() => removeTestimonial(i)} data-testid={`testimonial-remove-${stepIdx}-${i}`}><Trash2 size={9} /> Remove</button>
        </div>
      ))}
      <button type="button" className="fb-add-btn" onClick={addTestimonial} data-testid={`testimonial-add-${stepIdx}`}>
        <Plus size={11} /> Add testimonial
      </button>

      <div className="fb-section-hd"><Users size={11} /> Recent buyers — floating social-proof toasts ({buyers.length})</div>
      {buyers.map((b, i) => (
        <div className="fb-subblock" key={i} data-testid={`buyer-${stepIdx}-${i}`}>
          <div className="grid-2">
            <input className="f-input" placeholder="First-name + Last-initial" value={b.name || ''} onChange={(e) => updateBuyer(i, { name: e.target.value })} />
            <input className="f-input" placeholder="Location (e.g. Austin, TX)" value={b.location || ''} onChange={(e) => updateBuyer(i, { location: e.target.value })} />
          </div>
          <div className="grid-1" style={{ marginTop: 6 }}>
            <input className="f-input" placeholder="Time ago (e.g. 5 minutes ago)" value={b.time_ago || ''} onChange={(e) => updateBuyer(i, { time_ago: e.target.value })} />
          </div>
          <button type="button" className="dp-btn" style={{ marginTop: 4, color: 'var(--error)' }} onClick={() => removeBuyer(i)}><Trash2 size={9} /> Remove</button>
        </div>
      ))}
      <button type="button" className="fb-add-btn" onClick={addBuyer} data-testid={`buyer-add-${stepIdx}`}>
        <Plus size={11} /> Add buyer
      </button>

      <div className="fb-section-hd"><HelpCircle size={11} /> FAQ ({faq.length})</div>
      {faq.map((q, i) => (
        <div className="fb-subblock" key={i} data-testid={`faq-${stepIdx}-${i}`}>
          <input className="f-input" placeholder="Question" value={q.question || ''} onChange={(e) => updateFaq(i, { question: e.target.value })} />
          <textarea className="f-textarea" rows={3} style={{ marginTop: 6 }} placeholder="Answer (1-2 sentences)" value={q.answer || ''} onChange={(e) => updateFaq(i, { answer: e.target.value })} />
          <button type="button" className="dp-btn" style={{ marginTop: 4, color: 'var(--error)' }} onClick={() => removeFaq(i)} data-testid={`faq-remove-${stepIdx}-${i}`}><Trash2 size={9} /> Remove</button>
        </div>
      ))}
      <button type="button" className="fb-add-btn" onClick={addFaq} data-testid={`faq-add-${stepIdx}`}>
        <Plus size={11} /> Add FAQ
      </button>
    </>
  );
};

// ── Inline image upload (file picker → POST /api/funnels/upload-asset) ───────
const ImageUploadField = ({ value, onChange, placeholder, testid }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = React.useRef(null);

  const pick = () => inputRef.current?.click();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/funnels/upload-asset', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      onChange(data.url);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, position: 'relative' }}>
      {value && <img src={value} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
      <input className="f-input" style={{ flex: 1, fontSize: 11 }} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      <button type="button" className="dp-btn" onClick={pick} disabled={busy} title="Upload image" data-testid={testid}>
        {busy ? <Loader2 size={10} className="spin" /> : <Upload size={10} />}
      </button>
      <input ref={inputRef} type="file" accept="image/*" onChange={upload} style={{ display: 'none' }} />
      {err && <span style={{ position: 'absolute', top: '100%', left: 0, fontSize: 9, color: 'var(--error)' }}>{err}</span>}
    </div>
  );
};

// ── Inline video upload (file picker → POST /api/funnels/upload-video) ───────
const VideoUploadField = ({ value, onChange, placeholder, testid }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [progress, setProgress] = useState(0);
  const inputRef = React.useRef(null);

  const pick = () => inputRef.current?.click();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr(''); setProgress(0);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/funnels/upload-video', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 min for large videos
        onUploadProgress: (ev) => {
          if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
        },
      });
      onChange(data.url);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const isUploaded = value && value.startsWith('/api/uploads/');

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input
          className="f-input"
          type="url"
          style={{ flex: 1 }}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
        />
        <button
          type="button"
          className="dp-btn"
          onClick={pick}
          disabled={busy}
          title="Upload a video file (mp4, webm, mov — up to 100 MB)"
          data-testid={`${testid}-upload-btn`}
        >
          {busy ? <Loader2 size={10} className="spin" /> : <Upload size={10} />}
          <span style={{ marginLeft: 4 }}>{busy ? `${progress}%` : 'Upload'}</span>
        </button>
        <input ref={inputRef} type="file" accept="video/mp4,video/webm,video/quicktime,video/ogg,.mp4,.webm,.mov,.ogg,.m4v" onChange={upload} style={{ display: 'none' }} />
      </div>
      {isUploaded && (
        <div style={{ fontSize: 10, color: 'var(--success, #16a34a)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
          <PlayCircle size={10} /> Video uploaded — will autoplay muted on the public page.
        </div>
      )}
      {err && <div style={{ fontSize: 10, color: 'var(--error)', marginTop: 4 }}>{err}</div>}
    </div>
  );
};

// ── Opt-in fields editor — supports attaching an existing Form OR inline fields ──
const OptinFieldsEditor = ({ step, onChange, stepIdx }) => {
  const TYPES = ['text', 'email', 'phone', 'textarea', 'number'];
  const fields = step.optin_fields || [];
  const formId = step.form_id || '';
  const [forms, setForms] = useState([]);

  useEffect(() => {
    // Load all forms in this LOB. lob_id is implicit via the auth scope so the API
    // returns just what the current admin can see.
    api.get('/forms').then((r) => setForms(r.data || [])).catch(() => {});
  }, []);

  const setForm = (id) => {
    if (id) onChange({ form_id: id, optin_fields: [] });
    else onChange({ form_id: null });
  };
  const set = (idx, patch) => onChange({ optin_fields: fields.map((f, i) => i === idx ? { ...f, ...patch } : f) });
  const add = () => onChange({ optin_fields: [...fields, { id: crypto.randomUUID(), type: 'text', name: '', label: 'New field', required: false }] });
  const remove = (idx) => onChange({ optin_fields: fields.filter((_, i) => i !== idx) });

  const attachedForm = forms.find((f) => f.id === formId);

  return (
    <div style={{ marginTop: 10 }}>
      <div className="fb-section-hd"><Mail size={11} /> Lead capture form</div>

      <label className="f-label" style={{ marginTop: 2 }}>Use an existing form from the Forms module</label>
      <select
        className="f-select"
        value={formId}
        onChange={(e) => setForm(e.target.value)}
        data-testid={`step-form-${stepIdx}`}
      >
        <option value="">— Use the inline fields below —</option>
        {forms.map((f) => (
          <option key={f.id} value={f.id}>
            {f.title}{f.is_active === false ? ' (inactive)' : ''} — {f.fields?.length || 0} fields
          </option>
        ))}
      </select>

      {formId ? (
        <div className="fb-subblock" style={{ marginTop: 8 }} data-testid={`step-form-summary-${stepIdx}`}>
          {attachedForm ? (
            <>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{attachedForm.title}</div>
              <div style={{ fontSize: 11, color: 'var(--text-faded)', marginTop: 4 }}>
                {(attachedForm.fields || []).map((f) => f.label).join(' · ') || '(no fields yet)'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-faded)', marginTop: 8 }}>
                Edit this form's fields in the <b>Forms</b> module. Submissions made through this funnel
                will appear in both Funnels → Submissions <i>and</i> the form's own submissions list.
              </div>
            </>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--text-faded)' }}>Loading form…</div>
          )}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, fontWeight: 600, margin: '12px 0 6px' }}>Inline opt-in fields</div>
          {fields.map((f, i) => (
            <div key={f.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 110px 80px auto', gap: 6, alignItems: 'center', marginBottom: 6 }} data-testid={`optin-field-${stepIdx}-${i}`}>
              <input className="f-input" value={f.label} onChange={(e) => set(i, { label: e.target.value })} placeholder="Label" />
              <input className="f-input" value={f.name || ''} onChange={(e) => set(i, { name: e.target.value })} placeholder="Mapping (email, first, phone…)" />
              <select className="f-select" value={f.type} onChange={(e) => set(i, { type: e.target.value })}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <label style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="checkbox" checked={!!f.required} onChange={() => set(i, { required: !f.required })} />Req
              </label>
              <button type="button" className="dp-btn" onClick={() => remove(i)}><Trash2 size={9} /></button>
            </div>
          ))}
          <button type="button" className="fb-add-btn" onClick={add} data-testid={`optin-add-${stepIdx}`}><Plus size={10} /> Add field</button>
        </>
      )}
    </div>
  );
};

// ── Theme editor ───────────────────────────────────────────────────────────────
const ThemeEditor = ({ theme, onChange }) => {
  const [url, setUrl] = useState(theme.source_url || '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const set = (k, v) => onChange({ ...theme, [k]: v });

  const scrape = async () => {
    if (!url.trim()) return;
    setBusy(true); setMsg('');
    try {
      const { data } = await api.post('/funnels/scrape-theme', { url });
      onChange({ ...theme, ...data });
      setMsg('Theme refreshed.');
    } catch (e) { setMsg(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const SwatchPicker = ({ k, label }) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, marginBottom: 5 }}>
      <input type="color" value={theme[k] || '#000000'} onChange={(e) => set(k, e.target.value)} style={{ width: 24, height: 22, padding: 0, border: '0.5px solid var(--stroke-light)', borderRadius: 4, cursor: 'pointer' }} data-testid={`theme-${k}`} />
      <span style={{ flex: 1 }}>{label}</span>
      <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'var(--text-faded)' }}>{theme[k]}</span>
    </label>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
        <input className="f-input" style={{ fontSize: 11 }} placeholder="Re-scrape from URL" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="theme-scrape-url" />
        <button type="button" className="dp-btn" onClick={scrape} disabled={busy} data-testid="theme-scrape-btn">{busy ? '…' : <Globe size={10} />}</button>
      </div>
      {msg && <div style={{ fontSize: 9, color: 'var(--text-faded)', marginBottom: 6 }}>{msg}</div>}
      <SwatchPicker k="primary_color" label="Primary" />
      <SwatchPicker k="secondary_color" label="Secondary" />
      <SwatchPicker k="accent_color" label="Accent" />
      <SwatchPicker k="background_color" label="Background" />
      <SwatchPicker k="text_color" label="Text" />
      <label style={{ fontSize: 10, display: 'block', marginTop: 6 }}>Font family</label>
      <input className="f-input" style={{ fontSize: 10 }} value={theme.font_family || ''} onChange={(e) => set('font_family', e.target.value)} placeholder="Inter, system-ui, sans-serif" data-testid="theme-font" />
      <label style={{ fontSize: 10, display: 'block', marginTop: 6 }}>Logo (upload or paste URL)</label>
      <ImageUploadField
        value={theme.logo_url || ''}
        onChange={(url) => set('logo_url', url)}
        placeholder="https://…/logo.png"
        testid="theme-logo-upload"
      />
      <label style={{ fontSize: 10, display: 'block', marginTop: 6 }}>Favicon (upload or paste URL)</label>
      <ImageUploadField
        value={theme.favicon_url || ''}
        onChange={(url) => set('favicon_url', url)}
        placeholder="https://…/favicon.png"
        testid="theme-favicon-upload"
      />
    </div>
  );
};

// ── Submissions modal ──────────────────────────────────────────────────────────
const SubmissionsModal = ({ funnel, onClose }) => {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.get(`/funnels/${funnel.id}/submissions`).then((r) => setRows(r.data))
      .catch((e) => setErr(formatApiError(e)));
  }, [funnel.id]);
  const stepMap = useMemo(() => Object.fromEntries((funnel.steps || []).map((s) => [s.id, s])), [funnel]);

  return (
    <div className="modal-overlay-v2" onClick={onClose}>
      <div className="modal-card wide" onClick={(e) => e.stopPropagation()} data-testid="funnel-submissions-modal" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
        <div className="modal-title">{funnel.name} — submissions</div>
        {err && <div className="f-error">{err}</div>}
        {rows.length === 0 ? (
          <div className="empty-state"><div className="ttl">No submissions yet</div><div className="sub">Share the public URL to start capturing leads.</div></div>
        ) : (
          <table style={{ width: '100%', fontSize: 11 }}>
            <thead><tr style={{ textAlign: 'left', color: 'var(--text-faded)' }}>
              <th>When</th><th>Step</th><th>Data</th><th>Contact</th>
            </tr></thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} className="audit-row" data-testid={`sub-row-${s.id}`}>
                  <td>{new Date(s.submitted_at).toLocaleString()}</td>
                  <td>{stepMap[s.step_id]?.title || stepMap[s.step_id]?.type || '—'}</td>
                  <td>
                    {Object.entries(s.data || {}).map(([k, v]) => (
                      <div key={k}><b>{k}:</b> {String(v)}</div>
                    ))}
                  </td>
                  <td className="audit-meta">{s.contact_id ? s.contact_id.slice(0, 8) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default Funnels;
